/**
 * The monthly timer's notice settings (`cpc.statement_setting`, key 'notices'): what the
 * close check on func-cpc-statements does each day, and who hears about it
 * (src/statements/send/monthlyNotice.ts builds the emails). Saved from the Statements page
 * or `npm run statements:send -- --set-notices`, read by the Function App. Kept in SQL, not
 * app settings, so the addresses can be changed without a deploy (Yehuda, 2026-10-29).
 * No imports: the app, the API and the Function share it.
 */

export interface NoticeSettings {
  /** off: the timer does nothing. check: the close check and its notices. generate: also queues the month. */
  monthly: 'off' | 'check' | 'generate'
  /** The program owner, alerted each day the month is still not closed from `alertFromDay`. */
  owner: string | null
  /** The operator(s): one email per timer outcome, saying whether the owner's alert went out. */
  notify: string[]
  /** First day of the month the owner is alerted on (the days before payroll lands stay quiet). */
  alertFromDay: number
}

export const NO_NOTICES: NoticeSettings = { monthly: 'off', owner: null, notify: [], alertFromDay: 5 }

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Parse saved settings, refusing anything it does not understand rather than guessing. */
export function parseNotices(raw: unknown): NoticeSettings {
  if (raw === null || raw === undefined) return NO_NOTICES
  const o = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Record<string, unknown>
  if (typeof o !== 'object' || o === null || Array.isArray(o)) throw new Error('Notice settings must be an object.')
  const unknown = Object.keys(o).filter((k) => !['monthly', 'owner', 'notify', 'alertFromDay'].includes(k))
  if (unknown.length) throw new Error(`Unknown notice setting(s): ${unknown.join(', ')}.`)
  const monthly = o.monthly ?? 'off'
  if (monthly !== 'off' && monthly !== 'check' && monthly !== 'generate')
    throw new Error(`Notice setting "monthly" must be off, check or generate (got ${JSON.stringify(monthly)}).`)
  const owner = o.owner === undefined || o.owner === null || o.owner === '' ? null : o.owner
  if (owner !== null && (typeof owner !== 'string' || !EMAIL.test(owner.trim())))
    throw new Error('Notice setting "owner" must be an email address.')
  const notify = o.notify === undefined ? [] : o.notify
  if (!Array.isArray(notify) || notify.some((x) => typeof x !== 'string' || !EMAIL.test(x.trim())))
    throw new Error('Notice setting "notify" must be a list of email addresses.')
  const day = o.alertFromDay === undefined ? NO_NOTICES.alertFromDay : o.alertFromDay
  if (typeof day !== 'number' || !Number.isInteger(day) || day < 1 || day > 10)
    throw new Error('Notice setting "alertFromDay" must be a whole number from 1 to 10 (the days the check runs).')
  return {
    monthly,
    owner: owner === null ? null : owner.trim().toLowerCase(),
    notify: [...new Set((notify as string[]).map((x) => x.trim().toLowerCase()))],
    alertFromDay: day,
  }
}

/** The settings in one sentence, for the page. */
export function noticesSentence(s: NoticeSettings): string {
  const what =
    s.monthly === 'off'
      ? 'The monthly close check is off.'
      : s.monthly === 'check'
        ? 'The close check runs daily on the 1st to 10th and only reports.'
        : 'The close check runs daily on the 1st to 10th and queues the month once it closes.'
  const who = [
    s.owner ? `${s.owner} is alerted from the ${ordinal(s.alertFromDay)} while the month is not closed` : 'Nobody is alerted (no owner)',
    s.notify.length ? `${s.notify.join(', ')} ${s.notify.length === 1 ? 'gets' : 'get'} every outcome` : 'nobody gets the outcomes',
  ]
  return `${what} ${who.join('; ')}.`
}

const ordinal = (n: number) => `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`
